import React from 'react';
import { View } from 'react-native';

import {
  SubsectionForm,
  FormRow,
  FormColumn,
  FormInput,
  UnitNumberField,
} from '../../../../components/forms';
import { PermissionAwareField } from '../../../../components/PermissionAwareField';
import { SECTION_IDS } from '../../../../constants/sectionIds';

import type { SelectedFile } from '../../../../components/forms/FileUpload';
import type { SavedAttachment } from '../../../../services/attachmentService';
import type { Request } from '../../../../types';

type RacksInformationAttachmentBundle = {
  attachmentsBySection: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
};

export type RacksInformationProps = RacksInformationAttachmentBundle &
  (
    | {
        mode: 'initial';
        formData: Record<string, unknown>;
        updateFormData: (field: string, value: string) => void;
        requestId?: string;
        disabled: boolean;
        isEditing: boolean;
      }
    | {
        mode: 'persisted';
        request: Request;
        isEditing: boolean;
        editedData: Record<string, unknown>;
        updateEditedData: (field: string, value: string) => void;
        showFullDetails?: boolean;
      }
  );

function rackFieldDisplay(
  mode: 'initial' | 'persisted',
  field: string,
  formData: Record<string, unknown> | undefined,
  request: Request | undefined,
  editedData: Record<string, unknown> | undefined,
  isEditing: boolean,
): string {
  if (mode === 'initial' && formData) {
    const raw = formData[field];
    if (raw === undefined || raw === null) return '';
    return String(raw);
  }
  const reqRec = (request as unknown as Record<string, unknown>) ?? {};
  if (isEditing && editedData) {
    const raw = editedData[field] ?? reqRec[field];
    if (raw === undefined || raw === null) return '';
    return String(raw);
  }
  const raw = reqRec[field];
  if (raw === undefined || raw === null) return '';
  return String(raw);
}

/**
 * “Rack Dimensions” main card for create and opened-request layouts.
 */
export const RacksInformation: React.FC<RacksInformationProps> = props => {
  const {
    attachmentsBySection,
    onAttachFiles,
    onDeleteAttachment,
    selectedFiles,
    onFilesSelected,
    offlineMode,
    onCaptionChange,
    pendingCaptionEdits,
  } = props;

  if (props.mode === 'initial') {
    const { formData, updateFormData, requestId, disabled, isEditing } = props;
    const stage = formData.stage ?? formData.sysRequestStage;

    return (
      <SubsectionForm
        title="Rack Dimensions"
        sectionId={SECTION_IDS.RACKS_DIMENSIONS}
        requestId={requestId}
        allowAttachments={true}
        attachments={attachmentsBySection[SECTION_IDS.RACKS_DIMENSIONS] || []}
        onAttachFiles={() => onAttachFiles?.(SECTION_IDS.RACKS_DIMENSIONS)}
        onDeleteAttachment={onDeleteAttachment}
        isEditing={isEditing || !disabled}
        disabled={disabled}
        selectedFiles={selectedFiles[SECTION_IDS.RACKS_DIMENSIONS] || []}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(SECTION_IDS.RACKS_DIMENSIONS, files)
            : undefined
        }
        isOffline={offlineMode}
      >
        <FormRow>
          <FormColumn>
            <PermissionAwareField
              fieldId="rackDimensionsLength"
              requestStage={stage}
              requestId={requestId}
            >
              <UnitNumberField
              fieldKey="rackDimensionsLength"
                pair="lengthIn"
                label="Depth"
                value={rackFieldDisplay(
                  'initial',
                  'rackDimensionsLength',
                  formData,
                  undefined,
                  undefined,
                  false,
                )}
                onChangeText={value =>
                  updateFormData('rackDimensionsLength', value)
                }
                placeholder="Enter depth"
                disabled={disabled}
                readOnlyMode={disabled}
              />
            </PermissionAwareField>
          </FormColumn>
          <FormColumn>
            <PermissionAwareField
              fieldId="rackDimensionsHeight"
              requestStage={stage}
              requestId={requestId}
            >
              <FormInput
                label="Height [RU/OU]"
                value={rackFieldDisplay(
                  'initial',
                  'rackDimensionsHeight',
                  formData,
                  undefined,
                  undefined,
                  false,
                )}
                onChangeText={value =>
                  updateFormData('rackDimensionsHeight', value)
                }
                placeholder="Enter height"
                disabled={disabled}
                readOnlyMode={disabled}
              />
            </PermissionAwareField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <PermissionAwareField
              fieldId="rackDimensionsWidth"
              requestStage={stage}
              requestId={requestId}
            >
              <UnitNumberField
              fieldKey="rackDimensionsWidth"
                pair="lengthIn"
                label="Width"
                value={rackFieldDisplay(
                  'initial',
                  'rackDimensionsWidth',
                  formData,
                  undefined,
                  undefined,
                  false,
                )}
                onChangeText={value =>
                  updateFormData('rackDimensionsWidth', value)
                }
                placeholder="Enter width"
                disabled={disabled}
                readOnlyMode={disabled}
              />
            </PermissionAwareField>
          </FormColumn>
          <FormColumn>
            <View />
          </FormColumn>
        </FormRow>
      </SubsectionForm>
    );
  }

  const {
    request,
    isEditing,
    editedData,
    updateEditedData,
    showFullDetails = true,
  } = props;

  return (
    <SubsectionForm
      title="Rack Dimensions"
      sectionId={SECTION_IDS.RACKS_DIMENSIONS}
      requestId={request.id}
      allowAttachments={true}
      attachments={attachmentsBySection[SECTION_IDS.RACKS_DIMENSIONS] || []}
      onAttachFiles={() => onAttachFiles?.(SECTION_IDS.RACKS_DIMENSIONS)}
      onDeleteAttachment={onDeleteAttachment}
      isEditing={isEditing}
      disabled={!isEditing}
      readOnlyMode={!isEditing}
      selectedFiles={selectedFiles[SECTION_IDS.RACKS_DIMENSIONS] || []}
      onFilesSelected={
        onFilesSelected
          ? files => onFilesSelected(SECTION_IDS.RACKS_DIMENSIONS, files)
          : undefined
      }
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
    >
      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="rackDimensionsLength"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <UnitNumberField
              fieldKey="rackDimensionsLength"
              pair="lengthIn"
              label="Depth"
              value={rackFieldDisplay(
                'persisted',
                'rackDimensionsLength',
                undefined,
                request,
                editedData,
                isEditing,
              )}
              onChangeText={
                isEditing
                  ? value => updateEditedData('rackDimensionsLength', value)
                  : () => {}
              }
              placeholder="Enter depth"
              disabled={!isEditing}
              readOnlyMode={!isEditing}
            />
          </PermissionAwareField>
        </FormColumn>
        <FormColumn>
          <PermissionAwareField
            fieldId="rackDimensionsHeight"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <FormInput
              label="Height [RU/OU]"
              value={rackFieldDisplay(
                'persisted',
                'rackDimensionsHeight',
                undefined,
                request,
                editedData,
                isEditing,
              )}
              onChangeText={
                isEditing
                  ? value => updateEditedData('rackDimensionsHeight', value)
                  : () => {}
              }
              placeholder="Enter height"
              disabled={!isEditing}
              readOnlyMode={!isEditing}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="rackDimensionsWidth"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <UnitNumberField
              fieldKey="rackDimensionsWidth"
              pair="lengthIn"
              label="Width"
              value={rackFieldDisplay(
                'persisted',
                'rackDimensionsWidth',
                undefined,
                request,
                editedData,
                isEditing,
              )}
              onChangeText={
                isEditing
                  ? value => updateEditedData('rackDimensionsWidth', value)
                  : () => {}
              }
              placeholder="Enter width"
              disabled={!isEditing}
              readOnlyMode={!isEditing}
            />
          </PermissionAwareField>
        </FormColumn>
        <FormColumn>
          <View />
        </FormColumn>
      </FormRow>
    </SubsectionForm>
  );
};

RacksInformation.displayName = 'RacksInformation';
